import React from "react";
import "./CommonText.scss";

interface TextFieldProps {
  name?: string;
  value: string | number; // The value of the text field
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void; // Function to handle changes in the input field
  placeholder?: string; // Placeholder text for the input field
  type?: "text" | "password" | "email" | "number" | "tel" | "time" | "date"; // Type of the text field (default is 'text')
  inputRestriction?: "numbers" | "alphabets" | "alphanumeric" | "cash" | ""; // Restriction on input type (default is no restriction)
  className?: string; // Custom CSS class for styling
  disabled?: boolean; // Disable input field (default is false)
  error?: string; // Error message to display
  maxLength?: number; // Maximum length of input (default is unlimited)
  color?: string; // Text color (default is black)
  backgroundColor?: string; // Background color of the input (default is transparent)
  border?: string; // Border property (e.g., "2px solid #333")
  borderBottom?: string;
  borderTop?: string;
  borderRadius?: string; // Border radius of the input (default is '4px')
  icon?: React.ReactNode; // Icon to show inside the text field
  iconRight?: React.ReactNode;
  autoFocus?: boolean;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

const CommonText: React.FC<TextFieldProps> = ({
  value,
  name,
  onChange,
  placeholder = "",
  type = "text",
  inputRestriction = "",
  className = "",
  disabled = false,
  error = "",
  maxLength,
  color = "#000000",
  backgroundColor = "transparent",
  border = "1px solid #ccc",
  borderBottom = "",
  borderTop = "",
  borderRadius = "4px",
  icon,
  iconRight,
  autoFocus = false,
  onKeyDown,
}: TextFieldProps) => {

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
  
    switch (inputRestriction) {
      case "numbers":
        newValue = newValue.replace(/\D/g, "");
        break;
      case "alphabets":
        newValue = newValue.replace(/[^a-zA-Z\s]/g, "");
        break;
      case "alphanumeric":
        newValue = newValue.replace(/[^a-zA-Z0-9\.]/g, "");
        break;
      case "cash":
        newValue = newValue.replace(/[^0-9.]/g, ""); // Allow digits and dot
        // Only allow one dot
        const firstDotIndex = newValue.indexOf(".");
        if (firstDotIndex !== -1) {
          const beforeDot = newValue.slice(0, firstDotIndex);
          const afterDot = newValue.slice(firstDotIndex + 1).replace(/\./g, ""); // remove additional dots
          newValue = beforeDot + "." + afterDot;
          // Limit to 2 decimals if user has typed after dot
          if (afterDot.length > 2) {
            newValue = beforeDot + "." + afterDot.slice(0, 2);
          }
        }
        break;

      default:
        break;
    }
  
    if (onChange) {
      e.target.value = newValue;
      onChange(e);
    }
  };    

  return (
    <div
      className={`common-text-field-wrapper ${className}`}
      style={{ position: "relative" }}>
      {icon && (
        <div
          className="icon-container"
          style={{
            position: "absolute",
            left: "10px",
            top: "50%",
            transform: "translateY(-50%)",
          }}>
          {icon}
        </div>
      )}
      {iconRight && (
        <div
          className="icon-right-container"
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            cursor: "pointer",
          }}
        >
          {iconRight}
        </div>
      )}

      <input
        type={type}
        value={value}
        onChange={handleInputChange}
        name={name}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        autoFocus={autoFocus}
        style={{
          color,
          backgroundColor,
          border,
          borderRadius,
          paddingLeft: icon ? "30px" : "10px",
          paddingRight: iconRight ? "35px" : "10px",
        }}
        className={`common-text-field ${disabled ? "disabled" : ""}`}
      />
      {error && (
        <div className="text-start position-absolute common-error-message">
          {error}
        </div>
      )}
    </div>
  );
};

export default CommonText;
